import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Input, Stack } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import type { DriverCatalog } from '@casa/driver-host';
import { readForm, type Form, type FormField, type FormPath } from '@casa/driver-sdk';
import {
    PRIMARY_SOURCE,
    TAP_SOURCE,
    TILE_OPTIONS_SCHEMAS,
    TileTapSchema,
    commandTapJsonSchema,
    hasOwnTap,
    sourcesNamed,
    tileOptionsJsonSchema,
    tileSlots,
    type Connection,
    type Dashboard,
    type DataSource,
    type ScreenTile,
    type StylePreset,
    type TileType,
} from '@casa/model';
import { GroupTileView, TileView, tileStyleOf } from '@casa/tiles';
import { loadDrivers } from '../drivers/drivers.ts';
import { colorsUsedIn } from '../forms/color_fields.tsx';
import { ChoiceField, ScreenRow } from '../forms/form_rows.tsx';
import { FormView } from '../forms/form_view.tsx';
import { SettingsGroup } from '../settings/settings_ui.tsx';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { ChromeNote } from '../ui/chrome_page.tsx';
import { ConfirmDialog } from '../ui/confirm_dialog.tsx';
import { IconSearch } from './edit_dialogs.tsx';
import { catalogEntry } from './tile_catalog.ts';
import { SaveStyleRow, StylePickerRow } from './tile_style_picker.tsx';
import { TileStyleFields } from './tile_style_fields.tsx';
import { tileFieldDescriber, tileFieldRenderer, type TileOptionsContext } from './tile_options.tsx';

// A tile's configuration (dashboard-editor spec §5, Phase 3 E6a to E6c): its title and title icon,
// its options as a form made from its schema (T-4, §5.7), each slot's data chosen on a screen of its
// own from a connection's driver (T-8 to T-11), lists of slots (graph series, table rows, T-6),
// a preview with live data (T-7), and what a tap on it does (tiles spec TA-6). Valid changes go
// into the working copy at once, so the preview and the screen behind show them; Cancel puts back
// the working copy as it was when the dialog opened; the cross and Escape ask first when there is
// something to lose (issue #34). A new tile opens on its first slot's data (the wizard).

export interface TileDialogProps {
    shell: Bridge;
    dashboard: Dashboard;
    tile: ScreenTile;
    /** The tile's size on the screen, in pixels, for the previews of the styles to pick (tile-styles.md TY-5); unknown before the screen is laid out. */
    tileSize: { width: number; height: number } | undefined;
    /** The name of the screen the tile is on, which its tap does not offer. */
    screenName: string | undefined;
    /** Just added: the dialog starts on its data, and Cancel takes the tile away again. */
    isNew: boolean;
    /** Something to say about the tile first, such as that it was added over others on a full screen. */
    notice?: string | undefined;
    /** Whether the working copy differs from when the dialog opened: Cancel would throw something away. */
    hasChanges: boolean;
    onChange: (tile: ScreenTile) => void;
    /** One of the dashboard's own styles saved or renamed (tile-styles.md TY-9). */
    onSaveStyle: (style: StylePreset, previous?: string) => void;
    /** One of the dashboard's own styles for the tile's type deleted. */
    onDeleteStyle: (name: string) => void;
    onAddConnection: (name: string, connection: Connection) => void;
    onDone: () => void;
    onCancel: () => void;
}

const tileName = (tile: ScreenTile) : string => catalogEntry(tile.type).name.toLowerCase();

export function TileDialog(props: TileDialogProps) : ReactNode {
    const { tile, isNew, hasChanges, onChange, onDone, onCancel } = props;
    const [catalog, setCatalog] = useState<DriverCatalog>();
    const [problem, setProblem] = useState<string>();
    const [tapProblem, setTapProblem] = useState<string>();
    const [isAskingDiscard, setAskingDiscard] = useState(false);
    const unfinished = problem ?? tapProblem;

    useEffect(() => {
        void loadDrivers(props.shell).then(setCatalog);
    }, [props.shell]);

    // The cross and Escape are easily hit by accident; Cancel is a choice and closes at once (the owner, #34).
    const leave = () : void => {
        if (isNew || hasChanges) {
            setAskingDiscard(true);
        } else {
            onCancel();
        }
    };

    return (
        <>
            <ChromeDialog
                title={isNew ? `New ${tileName(tile)} tile` : `Edit "${tile.label ?? tileName(tile)}"`}
                onClose={leave}
                focusesField={false}
                fillsPhone
                footer={
                    <>
                        <Button variant="outline" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button colorPalette="blue" disabled={unfinished !== undefined} onClick={onDone}>
                            Done
                        </Button>
                    </>
                }
            >
                <Stack gap="6">
                    {props.notice ? <ChromeNote status="warning" text={props.notice} /> : null}
                    <Preview tile={tile} />
                    <SettingsGroup title="Title">
                        <Box px="4" py="3">
                            <Input
                                aria-label="Title"
                                placeholder="No title"
                                value={tile.label ?? ''}
                                onChange={(event) => {
                                    const label = event.target.value;
                                    const { label: _label, ...rest } = tile;
                                    onChange(label === '' ? (rest as ScreenTile) : { ...tile, label });
                                }}
                            />
                        </Box>
                        <TitleIconRow tile={tile} onChange={onChange} />
                    </SettingsGroup>
                    {tile.type === 'group' ? null : <OptionsForm {...props} tile={tile} catalog={catalog} onProblem={setProblem} />}
                    {tile.type === 'group' || hasOwnTap(tile) ? null : <TapGroup {...props} tile={tile} catalog={catalog} onProblem={setTapProblem} />}
                    <TileStyleRow tile={tile} dashboard={props.dashboard} tileSize={props.tileSize} onChange={onChange} onSaveStyle={props.onSaveStyle} onDeleteStyle={props.onDeleteStyle} />
                    {unfinished ? <ChromeNote status="warning" text={`Not finished: ${unfinished}`} /> : null}
                </Stack>
            </ChromeDialog>
            {isAskingDiscard ? (
                <ConfirmDialog
                    title={isNew ? 'Discard the new tile?' : 'Discard the changes?'}
                    message={isNew ? 'The new tile goes away again.' : 'The tile goes back to how it was before this dialog opened.'}
                    confirm="Discard"
                    cancel="Keep editing"
                    onConfirm={onCancel}
                    onCancel={() => setAskingDiscard(false)}
                />
            ) : null}
        </>
    );
}

/**
 * A style to pick (tile-styles.md TY-6), then the tile's style (dashboard-editor spec §5.9, TS-4): a
 * row opening a screen of its own, with the preview.
 */
function TileStyleRow({ tile, dashboard, tileSize, onChange, onSaveStyle, onDeleteStyle }: Pick<TileDialogProps, 'tile' | 'dashboard' | 'tileSize' | 'onChange' | 'onSaveStyle' | 'onDeleteStyle'>) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const own = tile.style ?? {};
    const set = Object.keys(own).length;
    const known = useMemo(() => colorsUsedIn(dashboard.screens), [dashboard.screens]);

    return (
        <SettingsGroup>
            <StylePickerRow tile={tile} dashboard={dashboard} tileSize={tileSize} title={catalogEntry(tile.type).name} onChange={onChange} onSaveStyle={onSaveStyle} onDeleteStyle={onDeleteStyle} />
            <ScreenRow
                label="Tile style"
                value={set === 0 ? "The dashboard's" : `${set} of its own`}
                isOpen={isOpen}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                screen={
                    <Stack gap="5">
                        <Preview tile={tile} />
                        <TileStyleFields
                            style={own}
                            fallback={tileStyleOf(dashboard.tileStyle, undefined)}
                            known={known}
                            hasText={tile.type !== 'group'}
                            hasValue={tile.type === 'value'}
                            // A tile's gauge style is edited with its gauge (issue #24).
                            onChange={(style) => {
                                const { style: _style, ...rest } = tile;
                                onChange((Object.keys(style).length > 0 ? { ...tile, style } : rest) as ScreenTile);
                            }}
                        />
                    </Stack>
                }
            />
            <SaveStyleRow tile={tile} dashboard={dashboard} tileSize={tileSize} onChange={onChange} onSaveStyle={onSaveStyle} />
        </SettingsGroup>
    );
}

/** The title icon, opening the icon search. */
function TitleIconRow({ tile, onChange }: { tile: ScreenTile; onChange: (tile: ScreenTile) => void }) : ReactNode {
    const [isOpen, setOpen] = useState(false);

    return (
        <ScreenRow
            label="Title icon"
            value={tile.titleIcon ?? 'None'}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <IconSearch
                    onPick={(name) => {
                        const { titleIcon: _titleIcon, ...rest } = tile;
                        onChange(name === undefined ? (rest as ScreenTile) : { ...tile, titleIcon: name });
                        setOpen(false);
                    }}
                />
            }
        />
    );
}

type OptionsTile = Exclude<ScreenTile, { type: 'group' }>;

/** A table's rows with a cell for each column: columns added or removed add or remove that cell (§5.7). */
function fitRowsToColumns(type: TileType, options: Record<string, unknown>) : Record<string, unknown> {
    if (type !== 'table' || !Array.isArray(options.columns) || !Array.isArray(options.rows)) {
        return options;
    }

    const count = options.columns.length;

    return { ...options, rows: (options.rows as unknown[][]).map((row) => Array.from({ length: count }, (_, index) => row[index] ?? '')) };
}

/** The title of the field at `path` in the form, for saying what is missing ("Series 1, Readings"). */
function titleAt(form: Form, path: readonly PropertyKey[]) : string {
    const parts: string[] = [];
    let fields: readonly FormField[] = [...form.fields, ...(form.alternatives ?? []).flatMap((alternative) => alternative.form.fields)];
    let itemTitle: string | undefined;
    for (const key of path) {
        if (typeof key === 'number') {
            parts.push(`${itemTitle ?? 'Item'} ${key + 1}`);
            continue;
        }

        const field = fields.find((each) => each.name === key);
        if (!field) {
            parts.push(String(key));
            break;
        }
        if (field.kind === 'list') {
            itemTitle = field.itemTitle;
            fields = [...field.item.fields, ...(field.item.alternatives ?? []).flatMap((alternative) => alternative.form.fields)];
        } else {
            parts.push(field.title);
            fields = field.kind === 'group' ? [...field.form.fields, ...(field.form.alternatives ?? []).flatMap((alternative) => alternative.form.fields)] : [];
        }
    }

    return parts.join(', ');
}

/** What is wrong with the options, in words: where, and what. */
function describeProblem(form: Form, error: { readonly issues: readonly { readonly path: readonly PropertyKey[]; readonly message: string }[] }) : string {
    const issue = error.issues[0];
    if (!issue) {
        return 'the options are not valid';
    }

    const where = titleAt(form, issue.path);
    const what = /received undefined/.test(issue.message) ? 'is not set' : issue.message;

    return where ? `${where} ${what}` : what;
}

interface OptionsFormProps extends TileDialogProps {
    tile: OptionsTile;
    catalog: DriverCatalog | undefined;
    onProblem: (problem: string | undefined) => void;
}

/**
 * The tile's options as a form (§5.7). Its value is kept here and checked against the schema on
 * each change; only valid options reach the tile, with the sources they name. A source an edit
 * left unused goes; one the dialog never touched stays, whatever names it.
 */
function OptionsForm({ shell, dashboard, tile, isNew, onChange, onAddConnection, catalog, onProblem }: OptionsFormProps) : ReactNode {
    const type = tile.type;
    const form = useMemo(() => readForm(tileOptionsJsonSchema(type)), [type]);
    const [value, setValue] = useState<Record<string, unknown>>(() => ({ ...tile.options }));
    const latest = useRef(tile);
    latest.current = tile;
    const sources = useRef<Record<string, DataSource>>({ ...tile.dataSources });
    const named = useRef(sourcesNamed(type, tile.options));
    const options = useRef(value);
    // The first slot to fill opens at once for a new tile (the wizard); a list's slots wait for an item,
    // and one that may stay empty (a text tile's reading, `whenUnset`) is not asked for.
    const opensFirst = useMemo(() => (isNew ? tileSlots(type).find((slot) => slot.whenUnset === undefined)?.path.join('/') : undefined), [isNew, type]);

    const commit = () : void => {
        const parsed = TILE_OPTIONS_SCHEMAS[type].safeParse(fitRowsToColumns(type, options.current));
        if (!parsed.success) {
            onProblem(describeProblem(form, parsed.error));

            return;
        }

        onProblem(undefined);
        const now = sourcesNamed(type, parsed.data);
        // The sources the options name, as chosen here; the others (the tap's, TA-6) as the tile has them now.
        const current = latest.current.dataSources;
        const dataSources: Record<string, DataSource> = {};
        for (const name of new Set([...Object.keys(current), ...Object.keys(sources.current)])) {
            const source = now.has(name) ? (sources.current[name] ?? current[name]) : named.current.has(name) ? undefined : current[name];
            if (source) {
                dataSources[name] = source;
            }
        }
        sources.current = dataSources;
        for (const name of now) {
            named.current.add(name);
        }
        onChange({ ...latest.current, options: parsed.data, dataSources } as ScreenTile);
    };
    const change = (next: Record<string, unknown>) : void => {
        const fitted = fitRowsToColumns(type, next);
        options.current = fitted;
        setValue(fitted);
        commit();
    };

    const context: TileOptionsContext = {
        shell,
        dashboard,
        catalog,
        sources: tile.dataSources,
        setSource: (name, source) => {
            sources.current = { ...sources.current, [name]: source };
            named.current.add(name);
        },
        commit,
        newSourceName: (path) => newSourceName(path, new Set([...Object.keys(sources.current), ...sourcesNamed(type, options.current)])),
        onAddConnection,
        opensFirst,
        knownColors: colorsUsedIn(dashboard.screens),
        tileStyle: tile.style,
        gaugeStyle: {
            fallback: tileStyleOf(dashboard.tileStyle, undefined).gaugeStyle,
            // Kept in the tile's style, edited with its gauge (issue #24).
            set: (gaugeStyle) => {
                const current = latest.current;
                const { gaugeStyle: _old, ...rest } = current.style ?? {};
                const style = gaugeStyle === undefined ? rest : { ...rest, gaugeStyle };
                const { style: _style, ...without } = current;
                onChange((Object.keys(style).length > 0 ? { ...current, style } : without) as ScreenTile);
            },
        },
    };
    return (
        <SettingsGroup title={catalogEntry(type).name}>
            <FormView form={form} value={value} onChange={change} renderField={tileFieldRenderer(context)} describeField={tileFieldDescriber(context)} />
        </SettingsGroup>
    );
}

const TAP_KINDS = [
    { value: 'none', label: 'Nothing' },
    { value: 'screen', label: 'Go to a screen' },
    { value: 'command', label: 'Send a command' },
] as const;

type TapKindChoice = (typeof TAP_KINDS)[number]['value'];

/** The data source a tile's tap sends its command to, with its name. */
function tapSourceOf(tile: OptionsTile) : { name: string; source: DataSource } | undefined {
    const name = tile.tap?.kind === 'command' ? tile.tap.source : undefined;
    const source = name === undefined ? undefined : tile.dataSources[name];

    return name !== undefined && source ? { name, source } : undefined;
}

interface TapGroupProps extends TileDialogProps {
    tile: OptionsTile;
    catalog: DriverCatalog | undefined;
    onProblem: (problem: string | undefined) => void;
}

/**
 * What a tap on the tile does (tiles spec TA-6): nothing, go to one of the other screens, or send a
 * command, chosen as an action tile's (its device, command and argument). The tap is kept here
 * while it is not finished; the tile gets it once it is. The command's data source is the tap's
 * own: changing the kind, or Nothing, takes it away.
 */
function TapGroup({ shell, dashboard, tile, screenName, onChange, onAddConnection, catalog, onProblem }: TapGroupProps) : ReactNode {
    const form = useMemo(() => readForm(commandTapJsonSchema()), []);
    const [value, setValue] = useState<Record<string, unknown> | undefined>(() => (tile.tap ? { ...tile.tap } : undefined));
    const latest = useRef(tile);
    latest.current = tile;
    const current = useRef(value);
    const own = useRef(tapSourceOf(tile));
    // The names of the sources the tap had or made: taken away with it, unless the options name them too.
    const tapSources = useRef(new Set(own.current ? [own.current.name] : [])).current;

    // A tap left unfinished does not hold up Done once the group is gone (a status tile given a command).
    useEffect(() => () => onProblem(undefined), [onProblem]);

    const write = (next: Record<string, unknown> | undefined) : void => {
        const tileNow = latest.current;
        const usedByOptions = sourcesNamed(tileNow.type, tileNow.options);
        const dataSources = Object.fromEntries(Object.entries(tileNow.dataSources).filter(([name]) => !tapSources.has(name) || usedByOptions.has(name)));
        if (next?.kind === 'command' && own.current) {
            dataSources[own.current.name] = own.current.source;
        }
        const { tap: _tap, ...rest } = tileNow;
        const parsed = next === undefined ? undefined : TileTapSchema.safeParse(next);
        if (parsed && !parsed.success) {
            onProblem(next?.kind === 'screen' ? 'On a tap, Screen is not chosen' : `On a tap, ${describeProblem(form, parsed.error)}`);
        } else {
            onProblem(undefined);
        }
        onChange({ ...rest, dataSources, ...(parsed?.success ? { tap: parsed.data } : {}) } as ScreenTile);
    };
    const change = (next: Record<string, unknown> | undefined) : void => {
        current.current = next;
        setValue(next);
        write(next);
    };
    const choose = (kind: TapKindChoice) : void => {
        if (kind === (value?.kind ?? 'none')) {
            return;
        }
        own.current = undefined;
        change(kind === 'none' ? undefined : { kind });
    };

    const context: TileOptionsContext = {
        shell,
        dashboard,
        catalog,
        sources: { ...tile.dataSources, ...(own.current ? { [own.current.name]: own.current.source } : {}) },
        setSource: (name, source) => {
            own.current = { name, source };
            tapSources.add(name);
        },
        commit: () => write(current.current),
        newSourceName: () => newSourceName([TAP_SOURCE], new Set(Object.keys(latest.current.dataSources))),
        onAddConnection,
        opensFirst: undefined,
        knownColors: colorsUsedIn(dashboard.screens),
        tileStyle: tile.style,
        gaugeStyle: { fallback: tileStyleOf(dashboard.tileStyle, undefined).gaugeStyle, set: () => {} },
    };
    const kind: TapKindChoice = value?.kind === 'screen' || value?.kind === 'command' ? value.kind : 'none';
    const screen = typeof value?.screen === 'string' ? value.screen : undefined;
    const others = dashboard.screens.map((each, index) => ({ name: each.name, index })).filter((each) => each.name !== screenName);
    const isMissing = screen !== undefined && !dashboard.screens.some((each) => each.name === screen);

    return (
        <Stack gap="3">
            <SettingsGroup title="On a tap">
                <ChoiceField
                    label="A tap"
                    options={TAP_KINDS}
                    value={kind}
                    onChange={(picked) => choose(TAP_KINDS.find((each) => each.value === picked)?.value ?? 'none')}
                    helper="What a tap on the tile does on the dashboard. In edit mode a tap selects the tile."
                />
                {kind === 'screen' ? (
                    <ChoiceField
                        label="Screen"
                        options={others.map((each) => ({ value: each.name, label: each.name, detail: `Screen ${each.index + 1}` }))}
                        value={screen}
                        onChange={(picked) => change(picked === undefined ? { kind: 'screen' } : { kind: 'screen', screen: picked })}
                        placeholder={others.length === 0 ? 'No other screen yet' : 'Choose…'}
                        helper="The screen a tap goes to."
                    />
                ) : null}
                {kind === 'command' ? (
                    <FormView form={form} value={value ?? {}} onChange={change} renderField={tileFieldRenderer(context)} describeField={tileFieldDescriber(context)} />
                ) : null}
            </SettingsGroup>
            {isMissing ? <ChromeNote status="warning" text={`The dashboard has no screen "${screen}": a tap does nothing. Choose another.`} /> : null}
        </Stack>
    );
}

/**
 * A name for a new source of the slot at `path` (§5.7): `primary` for the tile's own `source`, else
 * the nearest option's name (`series`, `pv`, `temperature`, `cell` in a table), with a number
 * when it is taken.
 */
export function newSourceName(path: FormPath, taken: ReadonlySet<string>) : string {
    const names = path.filter((key): key is string => typeof key === 'string');
    const nearest = [...names].reverse().find((key) => key !== 'source');
    const base = nearest === undefined ? PRIMARY_SOURCE : nearest === 'rows' ? 'cell' : nearest;
    if (!taken.has(base)) {
        return base;
    }

    let number = 2;
    while (taken.has(`${base}-${number}`)) {
        number++;
    }

    return `${base}-${number}`;
}

/** The tile as it looks, at about the size it has on the screen, with live data (T-7); it takes no taps, which would act on the device. */
function Preview({ tile }: { tile: ScreenTile }) : ReactNode {
    const ratio = Math.min(1.2, Math.max(0.5, tile.layout.height / tile.layout.width));
    const width = 180;

    return (
        <Box alignSelf="center" width={`${width}px`} height={`${Math.round(width * ratio)}px`} position="relative" pointerEvents="none" data-preview>
            {tile.type === 'group' ? <GroupTileView group={tile} /> : <TileView tile={tile} />}
        </Box>
    );
}
